import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShoppingCart, Truck, Package, TrendingUp } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { TradeFlowGlobe } from "./TradeFlowGlobe";

const fade = (delay = 0, y = 24) => ({
  initial: { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const PERSONAS = [
  {
    role: "buyer", icon: ShoppingCart, label: "Importer / Buyer",
    color: "text-blue-400", border: "border-blue-400/30", bgSoft: "bg-blue-400/[0.04]",
    headline: "Pay smart. Release fast.",
    line: "20–30d faster cash conversion. 30–45% working-capital lift.",
  },
  {
    role: "exporter", icon: Truck, label: "Exporter",
    color: "text-purple-400", border: "border-purple-400/30", bgSoft: "bg-purple-400/[0.04]",
    headline: "Faster receivables.",
    line: "60-75d cycles → 30-40d. 2–4× more capital turns per year.",
  },
  {
    role: "downstream", icon: Package, label: "Downstream Buyer",
    color: "text-cyan-400", border: "border-cyan-400/30", bgSoft: "bg-cyan-400/[0.04]",
    headline: "Plan better. Stock right.",
    line: "15–25% fewer stockouts. 10–20% less excess inventory.",
  },
  {
    role: "investor", icon: TrendingUp, label: "Investor",
    color: "text-[#22C55E]", border: "border-[#22C55E]/30", bgSoft: "bg-[#22C55E]/[0.04]",
    headline: "By invitation only.",
    line: "Confidential demo released to approved investors only.",
  },
];

export function HeroSection({ openModal }) {
  useEffect(() => { analytics.pageView("/"); }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Premium clean backdrop — NO container photo. Just gradients + globe. */}
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#050A07] via-[#070F0A] to-[#050A07]" />
      <div className="absolute inset-0 z-0 [background-image:radial-gradient(ellipse_at_top_right,rgba(34,197,94,0.18),transparent_55%)]" />
      <div className="absolute inset-0 z-0 [background-image:radial-gradient(ellipse_at_bottom_left,rgba(34,197,94,0.10),transparent_55%)]" />

      {/* Animated trade-flow globe */}
      <div className="absolute inset-0 z-[1]">
        <TradeFlowGlobe />
      </div>

      {/* Fine grid */}
      <div className="absolute inset-0 z-[2] opacity-[0.05] [background-image:linear-gradient(rgba(34,197,94,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(34,197,94,0.4)_1px,transparent_1px)] [background-size:60px_60px]" />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 pt-24 pb-16">
        <motion.div {...fade(0)} className="flex items-center gap-2 mb-5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
          <p className="text-[#22C55E] text-[10px] sm:text-xs font-semibold tracking-[0.3em] uppercase">
            Operating Infrastructure · Global Food Commerce
          </p>
        </motion.div>

        <motion.h1 {...fade(0.1)} className="font-['Outfit'] text-5xl sm:text-6xl lg:text-[88px] font-bold tracking-[-0.02em] text-white leading-[0.95] max-w-5xl">
          The infrastructure <br className="hidden md:block" />
          <span className="text-[#22C55E]">global food trade</span>
          <br className="hidden md:block" />
          flows through.
        </motion.h1>

        <motion.p {...fade(0.2)} className="mt-7 text-[#C5D5CC] text-base lg:text-lg max-w-2xl leading-relaxed">
          Connect the entire ecosystem — exporters, importers, distributors, retail buyers — into one
          intelligent operating layer. AI-scored counterparties, milestone-secured capital, real-time visibility.
          <span className="text-white"> Faster cycles. Lower risk. Compounded returns.</span>
        </motion.p>

        {/* Hero stats — glass tiles */}
        <motion.div {...fade(0.3)} className="mt-8 flex flex-wrap gap-4">
          {[["$8T+", "Addressable market"], ["$5B+", "Target trade flow"], ["75%", "Cycle reduction"], ["$144.5M", "Downstream pipeline"]].map(([v, l]) => (
            <div key={l} className="bg-white/[0.03] backdrop-blur-md border border-[#22C55E]/15 px-5 py-3 hover:border-[#22C55E]/40 transition-colors">
              <p className="font-['Outfit'] text-2xl font-bold text-white">{v}</p>
              <p className="text-[#A1B2A8]/80 uppercase tracking-wider text-[10px]">{l}</p>
            </div>
          ))}
        </motion.div>

        {/* 4-PERSONA CTA GRID */}
        <motion.div {...fade(0.4)} className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="persona-cta-grid">
          {PERSONAS.map((p) => (
            <motion.button
              key={p.role}
              onClick={() => { analytics.ctaClicked(`Apply as ${p.label}`, "hero"); openModal(p.role); }}
              whileHover={{ y: -6, scale: 1.005 }}
              transition={{ duration: 0.3 }}
              className={`group text-left border ${p.border} ${p.bgSoft} backdrop-blur-md hover:bg-white/[0.05] p-5 transition-all relative overflow-hidden`}
              data-testid={`hero-apply-${p.role}-btn`}
            >
              <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full ${p.role === "buyer" ? "bg-blue-400/10" : p.role === "exporter" ? "bg-purple-400/10" : p.role === "downstream" ? "bg-cyan-400/10" : "bg-[#22C55E]/10"} blur-2xl group-hover:opacity-80 opacity-50 transition-opacity`} />
              <div className="relative">
                <div className="flex items-start justify-between mb-3">
                  <p.icon className={`w-5 h-5 ${p.color}`} />
                  <span className={`text-[9px] uppercase tracking-wider font-semibold ${p.color}`}>{p.role === "investor" ? "Confidential" : "Apply"}</span>
                </div>
                <p className="font-['Outfit'] text-base sm:text-lg font-bold text-white mb-1 leading-tight">{p.headline}</p>
                <p className="text-[#A1B2A8] text-xs mb-4 leading-relaxed">{p.line}</p>
                <span className={`inline-flex items-center gap-1 text-[11px] ${p.color} font-semibold`}>
                  Apply as {p.label} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </motion.button>
          ))}
        </motion.div>

        <motion.div {...fade(0.5)} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
          <span className="text-[#A1B2A8]/60 tracking-wide uppercase text-[10px] flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-[#22C55E]" /> AI Confidence Engine on every deal
          </span>
          <span className="text-[#A1B2A8]/40">·</span>
          <span className="text-[#A1B2A8]/60 tracking-wide uppercase text-[10px]">Verified participants only</span>
          <span className="text-[#A1B2A8]/40">·</span>
          <span className="text-[#A1B2A8]/60 tracking-wide uppercase text-[10px]">Bank-grade payment rails</span>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050A07] to-transparent z-10" />
    </section>
  );
}
